/**
 * 케어센스 OAuth 설정
 */

import { API_BASE_URL } from '../AnyfitMall/config';
import { getBranding } from 'utils/branding';
import { Cookies } from 'react-cookie';

const cookies = new Cookies();

// OIDC 환경변수 검증
const validateOidcConfig = () => {
  const oidcEnvValue = process.env.REACT_APP_CARESENSE_USE_OIDC;

  if (!oidcEnvValue) {
    return false;
  }

  if (oidcEnvValue !== 'true' && oidcEnvValue !== 'false') {
    console.warn(
      `[Caresense] REACT_APP_CARESENSE_USE_OIDC는 "true" 또는 "false"여야 합니다. (현재: "${oidcEnvValue}")`,
    );
    return false;
  }

  return oidcEnvValue === 'true';
};

const useOpenIdConnect = validateOidcConfig();

export const CARESENSE_LOGOUT_URL = 'https://accounts.i-sens.com/oauth2/logout';

export const CARESENSE_CONFIG = {
  clientId: process.env.REACT_APP_CARESENSE_CLIENT_ID || 'test_client_id',
  authUrl: 'https://accounts.i-sens.com/auth/authorize',
  redirectUri:
    process.env.REACT_APP_CARESENSE_REDIRECT_URI ||
    `${window.location.origin}/caresense/callback`,
  apiBaseUrl: API_BASE_URL,
  // getter를 사용하여 토큰이 설정된 후 브랜딩 정보를 동적으로 가져옴
  get finalRedirectUrl() {
    const env = process.env.REACT_APP_ENV;
    const branding = getBranding();
    const redirects = branding.caresenseRedirect;

    switch (env) {
      case 'prod':
        return redirects.prod;
      case 'release':
        return redirects.release;
      case 'qa':
        return redirects.qa;
      case 'dev':
      default:
        return redirects.dev;
    }
  },
  useOpenIdConnect,
};

/**
 * 케어센스 OAuth 인증 URL 생성
 */
export const generateCaresenseAuthUrl = (useOpenIdConnect = false) => {
  const state = generateSecureRandom();
  const nonce = generateSecureRandom();

  // State를 Cookie에 저장 (5분 만료, SameSite=Lax)
  cookies.set('caresense_oauth_state', state, {
    path: '/',
    maxAge: 300,
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  });

  const isDev = ['local', 'dev'].includes(process.env.REACT_APP_ENV);

  if (isDev) {
    console.log('[Caresense] State 생성 및 Cookie 저장:', {
      state,
      cookie_확인: cookies.get('caresense_oauth_state'),
    });
  }

  // OIDC 모드일 때 nonce도 Cookie에 저장
  if (useOpenIdConnect) {
    cookies.set('caresense_oauth_nonce', nonce, {
      path: '/',
      maxAge: 300,
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
    });

    if (isDev) {
      console.log('[Caresense] Nonce 생성 (OIDC 모드)');
    }
  }

  // OAuth 파라미터 구성
  const params = {
    client_id: CARESENSE_CONFIG.clientId,
    redirect_uri: CARESENSE_CONFIG.redirectUri,
    response_type: 'code',
    state,
  };

  if (useOpenIdConnect) {
    params.scope = 'openid offline_access';
    params.nonce = nonce;
  }

  // redirectUri 유효성 검증
  try {
    new URL(CARESENSE_CONFIG.redirectUri);
  } catch (error) {
    throw new Error(`Invalid redirectUri: ${CARESENSE_CONFIG.redirectUri}`);
  }

  const searchParams = new URLSearchParams(params);
  return `${CARESENSE_CONFIG.authUrl}?${searchParams.toString()}`;
};

/**
 * 보안 랜덤 값 생성 (16바이트 = 32자 hex)
 */
const generateSecureRandom = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
};

/**
 * 케어센스 로그아웃 URL 생성
 * OAuth 인증 전 기존 세션을 정리하기 위해 사용
 */
export const generateCaresenseLogoutUrl = () => {
  // 로그아웃 후 리다이렉트될 URI 설정
  const postLogoutRedirectUri =
    process.env.REACT_APP_CARESENSE_LOGOUT_REDIRECT_URI ||
    `${window.location.origin}/caresense/logout-callback`;

  const params = new URLSearchParams({
    client_id: CARESENSE_CONFIG.clientId,
    post_logout_redirect_uri: postLogoutRedirectUri,
  });
  return `${CARESENSE_LOGOUT_URL}?${params.toString()}`;
};
